import React, { useState } from "react";

type Tab = {
  label: string;
  content: React.ReactNode;
  /** When true, a small red dot is shown next to this tab's label. */
  showIndicator?: boolean;
};

interface TabsProps {
  tabs: Tab[];
  onTabChange?: (index: number) => void;
  // Add an optional prop for custom styling of the content wrapper
  contentClassName?: string;
  barClassName?: string;
  /**
   * Optional controlled active-tab index. When provided the parent owns the
   * active tab (pair it with onTabChange); when omitted the component manages
   * its own state exactly as before, so existing usages are unaffected.
   */
  activeIndex?: number;
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  onTabChange,
  contentClassName = "pt-4", // Default to pt-4 to keep common usage unaffected
  barClassName = "",
  activeIndex,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(0);

  // Controlled when a numeric activeIndex is supplied; otherwise uncontrolled.
  const isControlled = typeof activeIndex === "number";
  const rawActive = isControlled ? (activeIndex as number) : internalActiveTab;
  // Clamp so an out-of-range index (e.g. after the tab list shrinks) is safe.
  const activeTab = Math.min(Math.max(rawActive, 0), Math.max(tabs.length - 1, 0));

  const handleTabChange = (index: number) => {
    if (!isControlled) setInternalActiveTab(index);
    onTabChange?.(index);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className={`sticky top-0 z-50 bg-white dark:bg-gray-800 ${barClassName}`}>
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`relative px-4 py-2 text-sm font-medium transition-colors ${activeTab === index
                  ? "border-b-2 border-primary text-primary"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              onClick={() => handleTabChange(index)}
            >
              <span className="relative inline-flex items-center">
                <span
                  style={
                    tab.label === "Profile"
                      ? { letterSpacing: "0.01em", fontVariantLigatures: "none" }
                      : undefined
                  }
                >
                  {tab.label}
                </span>
                {tab.showIndicator && (
                  <span
                    aria-hidden="true"
                    title="Action needed"
                    className="absolute -top-1 -right-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800"
                  />
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Apply the dynamic contentClassName here */}
      <div className={`flex-1 min-h-0 ${contentClassName}`}>
        {tabs[activeTab]?.content}
      </div>
    </div>
  );
};

export default Tabs;